import { useEffect, useState } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISSED_KEY = "install_prompt_dismissed";

function detectIOS(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function detectFirefoxAndroid(): boolean {
  return (
    /firefox/i.test(navigator.userAgent) && /android/i.test(navigator.userAgent)
  );
}

export interface UseInstallPromptResult {
  canInstall: boolean;
  isIOS: boolean;
  isFirefoxAndroid: boolean;
  triggerInstall: () => Promise<void>;
  dismiss: () => void;
}

export function useInstallPrompt(): UseInstallPromptResult {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISSED_KEY) === "true",
  );
  const [ios] = useState(() => detectIOS());
  const [firefoxAndroid] = useState(() => detectFirefoxAndroid());

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const canInstall =
    !dismissed && (deferredPrompt !== null || ios || firefoxAndroid);

  const triggerInstall = async () => {
    if (!deferredPrompt) return;
    try {
      await deferredPrompt.prompt();
      await deferredPrompt.userChoice;
    } finally {
      setDeferredPrompt(null);
    }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, "true");
    setDismissed(true);
  };

  return {
    canInstall,
    triggerInstall,
    dismiss,
    isIOS: ios,
    isFirefoxAndroid: firefoxAndroid,
  };
}
